import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
  ClickAwayListener,
  Grow,
  MenuList,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import {colors} from '@/constants'
import {ReactComponent as NotificationIcon} from '@/assets/svgs/header/NotificationIcon.svg'

import {NotificationInterface, UserInterface} from '@/types/types'
import Popper from '@mui/material/Popper'
import useFetching from '@/hooks/useFetching'
import UserService from '@/services/api/User'
import NotificationsList from '@/components/UserNavMenu/Notifications/NotificationsList'
import SimpleLoader from '@/components/UI/simpleLoader'
import useNotification from '@/hooks/notification'
import {useAppDispatch} from '@/hooks/redux'
import {
  SET_DOMINION,
  SET_HEXA,
  SET_OCTA,
  SET_REEX,
  SET_TERRA,
} from '@/store/slices/wallet'
import {errorCodeHandler} from '@/helpers/localErrorCodeHandler'
import TransCustom from '@/components/UI/i18next'
import useAcceptModal from '@/hooks/modalAccept'
import RewardsModal from '@/components/Modals/RewardsModal'
import AcceptModal from '@/components/AcceptModal'

const Index = ({user}: {user: UserInterface}) => {
  const {lastMessage} = useNotification()
  const [unreadCount, setUnreadCount] = useState(0)
  const {setAcceptModal} = useAcceptModal()
  const [notifications, setNotifications] = useState<NotificationInterface[]>(
    []
  )
  const dispatch = useAppDispatch()
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState(false)
  const [nextPaging, setNextPaging] = useState<null | string>(null)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [isLoadLocal, setIsLoadLocal] = useState<boolean>(false)
  const scrollRef = useRef(false)

  const [getNotifications, isLoading] = useFetching(
    async (nextPaging: string | null, countPage: number = 10) => {
      const response = await UserService.getNotifications(countPage, nextPaging)
      if (response?.status === 200) {
        setTimeout(() => {
          scrollRef.current = false
        }, 500)
        const reward = response.data.notifications.find(
          (notification) => notification.type === 5 && !notification.is_read
        )
        if (reward) {
          setAcceptModal(
            <AcceptModal
              firework={true}
              padding="60px 60px"
              handleAgree={() => readNotifications({ids: [reward.id]})}
              withoutCancel={true}>
              <RewardsModal text={reward.data.description} />
            </AcceptModal>
          )
        }
        if (nextPaging) {
          setNotifications((v) => [...v, ...response.data.notifications])
        } else {
          if (response.data.unread_count > 10) {
            getNotifications(
              response.data.next_paging_state,
              response.data.unread_count - 10
            )
          }
          setNotifications(response.data.notifications)
        }
        setNextPaging(response.data.next_paging_state)
        setUnreadCount(response.data.unread_count)
      }
    }
  )

  const readNotifications = useCallback(
    async (data: {ids?: string[]; mark_all_as_read?: boolean}) => {
      try {
        const response = await UserService.readNotifications(data)
        if (response.status === 200) {
          const id = data.ids ? data.ids[0] : undefined
          if (id) {
            setNotifications((list: NotificationInterface[]) => {
              const index = list.findIndex((v) => v.id === id)
              if (index !== -1) {
                const list_ = [...list]
                list_[index].is_read = true
                return list_
              }
              return list
            })
            setUnreadCount((v) => v - 1)
          }

          if (data.mark_all_as_read) {
            setNotifications((list: NotificationInterface[]) => {
              list.forEach((v, index) => {
                if (!v.is_read) {
                  list[index].is_read = true
                }
              })
              return list
            })
            setUnreadCount(0)
          }
        }
      } catch (e) {
        errorCodeHandler(e)
      }
    },
    []
  )

  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.data) {
        if (lastMessage.data.hexa) {
          dispatch(SET_HEXA(lastMessage.hexa || lastMessage.data.hexa))
        }
        if (lastMessage.data.reex) {
          dispatch(SET_REEX(lastMessage.data.reex))
        }
        if (lastMessage.data.terra) {
          dispatch(SET_TERRA(lastMessage.data.terra))
        }
        if (lastMessage.data.dominion) {
          dispatch(SET_DOMINION(lastMessage.data.dominion))
        }
        if (lastMessage.data.octa) {
          dispatch(SET_OCTA(lastMessage.data.octa))
        }
        if (lastMessage.data.type === 'NEW_REWARDS') {
          setUnreadCount((v) => v + 1)
        }
      } else {
        if (lastMessage.hexa) {
          dispatch(SET_HEXA(lastMessage.hexa))
        }
        if (lastMessage.reex) {
          dispatch(SET_REEX(lastMessage.reex))
        }
        if (lastMessage.terra) {
          dispatch(SET_TERRA(lastMessage.terra))
        }
        if (lastMessage.dominion) {
          dispatch(SET_DOMINION(lastMessage.dominion))
        }
        if (lastMessage.octa) {
          dispatch(SET_OCTA(lastMessage.octa))
        }
        if (lastMessage.type === 'NEW_REWARDS') {
          setUnreadCount((v) => v + 1)
        }
      }
    }
  }, [lastMessage])

  useEffect(() => {
    if (page !== 1) {
      getNotifications(nextPaging)
    }
  }, [page])

  useEffect(() => {
    getNotifications()
  }, [])

  const handleToggle = () => {
    setOpen((prevOpen) => {
      if (!prevOpen) {
        getNotifications(null)
      }

      return !prevOpen
    })
  }

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  useEffect(() => {
    if (isLoading) {
      setIsLoadLocal(true)
    } else {
      const timer = setTimeout(() => {
        setIsLoadLocal(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  return (
    <>
      <NotificationSVG
        ref={anchorRef}
        id="wallet-button"
        aria-controls={open ? 'wallet-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        position="relative">
        <NotificationIcon
          style={{
            marginRight: '5px',
            fill: open ? colors.orange : colors.white,
          }}
        />
        {unreadCount > 0 && (
          <Stack
            width="20px"
            border={`1px solid ${colors.brown}`}
            height="20px"
            borderRadius="50%"
            position="absolute"
            right="0"
            bgcolor={colors.yellow}>
            <Typography fontSize="13px" color={colors.brown} textAlign="center">
              {unreadCount}
            </Typography>
          </Stack>
        )}
      </NotificationSVG>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        style={{zIndex: 1300}}>
        {({TransitionProps}) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'right top',
              marginTop: '10px',
              borderRadius: '20px',
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="wallet-menu"
                  aria-labelledby="wallet-button"
                  style={{
                    padding: '0',
                    background: colors.brown,
                    color: colors.white,
                    boxShadow: '0px 0px 10px 0px #FFF17233',
                    width: '500px',
                    borderRadius: '20px',
                    border: `1px solid ${colors.lightBrown}`,
                  }}>
                  <Stack direction="column" gap="20px">
                    <Stack padding="20px 20px 0 20px">
                      <TransCustom
                        i18nKey="1418"
                        variant="button"
                        color={colors.white}
                        paddingBottom="20px"
                        borderBottom={`1px solid ${colors.lightBrown}`}
                        style={{textTransform: 'uppercase'}}
                      />
                    </Stack>
                    <TransCustom
                      i18nKey="1419"
                      variant="body2"
                      padding="0 20px"
                      className="pointer text-hover"
                      style={{textDecoration: 'underline'}}
                      onClick={() =>
                        readNotifications({mark_all_as_read: true})
                      }
                    />
                    <Stack
                      padding="0 5px 0 0"
                      width="100%"
                      height="300px"
                      boxSizing="border-box">
                      {isLoadLocal ? (
                        <Stack height="100%">
                          <SimpleLoader isLoad={isLoadLocal} local={true} />
                        </Stack>
                      ) : notifications.length > 0 ? (
                        <NotificationsList
                          nextPaging={nextPaging}
                          notifications={notifications}
                          scrollRef={scrollRef}
                          readNotification={readNotifications}
                          setPage={setPage}
                        />
                      ) : (
                        <TransCustom
                          i18nKey="1420"
                          textAlign="center"
                          marginTop="20px"
                        />
                      )}
                    </Stack>
                  </Stack>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default Index

const NotificationSVG = styled(Stack)`
  &:hover {
    cursor: pointer;

    & > svg {
      fill: ${colors.orange} !important;
    }
  }
`
